import { useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
	AppBar,
	Badge,
	badgeClasses,
	Box,
	Button,
	buttonBaseClasses,
	Container,
	Icon,
	IconButton,
	Link,
	SpeedDial,
	SpeedDialAction,
	SpeedDialIcon,
	speedDialIconClasses,
	styled,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery
} from '@mui/material';
import queryString from 'query-string';

import Files from 'Lib/components/common/table/controls/Files';
import { useFiles } from 'Lib/components/common/table/filesTable/context';
import SidebarContext from 'Lib/contexts/SidebarContext';
import Dialog from 'Lib/muiComponents/dialog/Dialog';
import SidebarButtons from 'Lib/muiComponents/sidebar';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

import { SavedSearch, Search, Settings } from '../common/table/controls';
import { VIEWS } from '../common/table/controls/TableView';
import MobilePagination from '../common/table/MobilePagination';

import { DRAWER_CONTENT_TYPES } from './components/SideDrawer';

const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

export default function ActionBar(props) {
	const history = useHistory();
	const dispatch = useDispatch();
	const listViewID = useSelector(state => state.ui.table.listViewID);
	const showActionsLabel = useMediaQuery('(min-width:320px)');

	const { mobile } = screenSize();
	const showListControls = !!props.fetch;

	const { apis, common, components, filters, sidebar = {}, actions, pagination, handlePageClick, hasPermission } = props;

	// Pulls values (page, pageSize, etc.) out of the queryString that we do not want present in the active filters. DO NOT REMOVE
	const { page, pageSize, search, orderBy, tab, ...activeFilters } = queryString.parse(history.location.search);
	const filterCount = Object.keys(activeFilters).length;

	const [open, setOpen] = useState(false);
	const [openDialog, setOpenDialog] = useState(false);

	const pagePadding = `.page-container { padding-bottom:${0} }`;

	const SingleAction = () => {
		const { label, icon, to, onClick, mobileLabel, ...rest } = actions[0];

		return (
			<Tooltip title={label} arrow placement="top">
				<Button
					{...rest}
					variant="primary"
					startIcon={icon && <Icon sx={{ fontSize: '25px !important' }}>{icon}</Icon>}
					onClick={e => (onClick ? onClick(e) : history.push(to))}
					sx={theme => ({
						height: 40,
						fontSize: theme.typography.pxToRem(14),
						borderRadius: '12px',
						right: 8,
						fontWeight: 600,
						ml: 'auto'
					})}>
					{mobileLabel ?? label}
				</Button>
			</Tooltip>
		);
	};

	const toggleModal = () => {
		setOpenDialog(prevState => !prevState);
	};

	const ActionsModal = () => {
		if (isEmpty(props.actions) || actions.render) {
			return null;
		}

		return (
			<Dialog
				contentSx={{ pt: 0 }}
				title={() => (
					<Container sx={{ display: 'flex', alignItems: 'center', pb: '0', mt: 1 }}>
						<IconButton onClick={toggleModal}>arrow_back</IconButton>
						<Typography
							sx={theme => ({
								fontSize: theme.typography.pxToRem(24)
							})}>
							Quick Actions
						</Typography>
					</Container>
				)}
				toggle={toggleModal}
				open={openDialog}
				icon="arrow_back"
				fullscreen>
				<Box display="flex" flexDirection="column" alignItems="start" justifyContent="start">
					{actions &&
						actions
							.filter(action => action.label)
							.map(({ label, to, onClick }) => {
								if (to) {
									return (
										<Link sx={{ my: 2 }} underline="none" key={label} component={RouterLink} to={to}>
											{label}
										</Link>
									);
								} else if (onClick) {
									return (
										<Button
											sx={{ my: 2, p: 0, justifyContent: 'start' }}
											key={label}
											onClick={e => {
												onClick(e);
												toggleModal();
											}}>
											<Typography sx={{ color: 'common.blue' }}>{label}</Typography>
										</Button>
									);
								}
							})}
				</Box>
			</Dialog>
		);
	};

	const { handleSearch = null, onDropFiles = null, disableUpload = false } = useFiles() || {};
	const fileUploadRef = useRef();
	const { getRootProps, getInputProps } = useDropzone({
		onDrop: onDropFiles,
		noClick: true,
		onFocus: null,
		onBlur: null
	});

	const renderMainActions = () => {
		if (props.files) {
			const { common } = useFiles();

			return (
				<Box {...getRootProps()} display="flex">
					<Search {...common} listTypeID={props.listTypeID} placeholder={props.placeholder} search={handleSearch} />
					<input {...getInputProps({ ref: fileUploadRef })} style={{ display: 'none' }} />
					<Files disableUpload={disableUpload} openFileUpload={() => fileUploadRef.current.click()} />
				</Box>
			);
		} else if (props.sidebar) {
			return (
				<Box display="flex">
					{props.sidebar.items.map((item, index) => {
						if (typeof item === 'object') {
							return (
								<IconButton key={index} onClick={item.onclick} title={item.title}>
									{item.icon}
								</IconButton>
							);
						} else {
							const Button = SidebarButtons[item];

							if (item === 'divider') {
								return null;
							}

							return (
								<SidebarContext.Provider value={{ sidebar, apis, components, hasPermission }} key={index}>
									<Button toggleDrawer={actionType => dispatch(common.actions.toggleDrawer(actionType))} />
								</SidebarContext.Provider>
							);
						}
					})}
				</Box>
			);
		} else if (showListControls) {
			return (
				<Box>
					{/* Search */}
					<Search {...common} listTypeID={props.listTypeID} placeholder={props.placeholder} search={props.fetch} />

					{/* Filters */}
					{filters && (
						<Badge color="primary" overlap="circular" badgeContent={filterCount} sx={{ [`& .${badgeClasses.badge}`]: { top: '5%' } }}>
							<IconButton
								aria-label="open filters"
								onClick={() => dispatch(common.actions.toggleDrawer(DRAWER_CONTENT_TYPES.FILTERS))}
								sx={{
									...(filterCount && {
										bgcolor: '#D2EBF6',
										color: '#0B4377',
										borderRadius: '10px'
									})
								}}>
								{filterCount ? 'filter_alt' : 'filter_alt_off'}
							</IconButton>
						</Badge>
					)}

					{/* Saved Searches */}
					<SavedSearch {...common} fetch={props.fetch} />

					{/* Settings */}
					{listViewID == VIEWS.LIST && <Settings actions={common.actions} />}
				</Box>
			);
		} else return null;
	};

	return (
		mobile && (
			<>
				<AppBar
					elevation={0}
					position="sticky"
					color="primary"
					component="footer"
					className=""
					sx={{
						bgcolor: 'common.offwhite',
						top: 'auto',
						justifyContent: 'center',
						bottom: 0,
						zIndex: 8,
						borderTop: theme => `1px solid ${theme.palette.common.darkgray}`
					}}>
					{/* Portal container that is used inside <ListTableToolbar /> */}
					<Box id="selected-table-toolbar" />
					{pagination && <MobilePagination pagination={pagination} onPageChange={handlePageClick} actions={common?.actions} />}

					<Toolbar
						sx={{
							display: 'flex',
							justifyContent: 'space-between'
						}}
						className="minimized-hidden">
						{renderMainActions()}
						{actions && actions.render ? (
							actions.render()
						) : !actions ? (
							<div />
						) : actions.length == 1 ? (
							<SingleAction />
						) : (
							<SpeedDial
								FabProps={{ variant: 'extended' }}
								ariaLabel="actions-speed-dial"
								sx={{
									position: 'absolute',
									bottom: 7,
									right: 8,
									mr: 2,
									[`& .${buttonBaseClasses.root}`]: {
										bgcolor: 'success.main',
										borderRadius: 3,
										height: 40,
										boxShadow: 'none',
										'&:hover': {
											bgcolor: '#029745'
										}
									},
									[`& .${speedDialIconClasses.root}`]: {
										height: 40,
										display: 'flex',
										alignItems: 'center'
									}
								}}
								icon={
									<SpeedDialIcon
										icon={
											<Typography
												color="common.white"
												fontSize="14px"
												sx={{
													fontWeight: 600,
													transform: 'none !important',
													textTransform: 'none'
												}}>
												{showActionsLabel ? 'Actions' : '+'}
											</Typography>
										}
									/>
								}
								onClick={() => {
									if (actions.length > 5) {
										toggleModal();
									} else {
										setOpen(true);
									}
								}}
								onClose={() => setOpen(false)}
								open={open}>
								{actions
									.filter(action => action.label)
									.map(({ label, icon, to, onClick }) => (
										<SpeedDialAction
											key={label}
											icon={<Icon sx={{ color: 'common.white' }}>{icon}</Icon>}
											tooltipTitle={label}
											onClick={e => (onClick ? onClick(e) : history.push(to))}
											tooltipOpen
										/>
									))}
							</SpeedDial>
						)}
					</Toolbar>
				</AppBar>

				{/* <Toolbar /> */}

				<ActionsModal />
				<style>{pagePadding}</style>
			</>
		)
	);
}
